import React from 'react';

/**
 * ProductGrid - Two-tier navigation (type tabs + sub-category tabs).
 * Products/Vouchers render as grid tiles. Services render as grouped list.
 */
const ProductGrid = ({
  products, serviceCategories, vouchers, featuredServices,
  activeType, activeSubCategory, onSubCategoryChange,
  productCategories, serviceCategoryTabs,
  onAddProduct, onAddService, onAddVoucher, lastAddedId,
  loading, currencySymbol, searchQuery, hasVouchers,
}) => {
  const fmt = (amount) => `${currencySymbol}${parseFloat(amount).toFixed(2)}`;
  // A product's price for its tile: its own, else the cheapest of its options
  // ("From £x"), else "No price". Never "£NaN".
  const priceOf = (product) => {
    const own = parseFloat(product.retailPrice != null && product.retailPrice !== '' ? product.retailPrice : product.price);
    if (Number.isFinite(own)) return fmt(own);
    const opts = (product.variants || []).map(v => parseFloat(v.retailPrice)).filter(Number.isFinite);
    if (opts.length) return `From ${fmt(Math.min(...opts))}`;
    return 'No price';
  };
  // Two letters for a tile with no photo ("Black Coffee" → "BC")
  const initialsOf = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '·';

  // Sub-category tabs for current type
  const subTabs = activeType === 'products'
    ? [{ id: 'all', label: 'All' }, ...productCategories.map((c, i) => ({ id: c._id || c.categoryId || c.id || `cat-${i}`, label: c.categoryName || c.name || c.category }))]
    : activeType === 'services'
    ? [{ id: 'all', label: 'All' }, ...serviceCategoryTabs]
    : null;

  return (
    <div className="pos-grid">
      {/* Sub-category tabs (only for products/services) */}
      {subTabs && subTabs.length > 1 && (
        <div className="pos-sub-tabs">
          {subTabs.map(tab => (
            <button
              key={tab.id}
              className={`pos-sub-tab ${activeSubCategory === tab.id ? 'pos-sub-tab--active' : ''}`}
              onClick={() => onSubCategoryChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="pos-tiles">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="pos-tile pos-tile--skeleton">
              <div className="pos-tile-skeleton-img" />
              <div className="pos-tile-skeleton-text" />
              <div className="pos-tile-skeleton-price" />
            </div>
          ))}
        </div>
      )}

      {/* Product tiles */}
      {!loading && activeType === 'products' && (
        <div className="pos-tiles">
          {products.length === 0 ? (
            <div className="pos-empty">
              <p>{searchQuery ? 'No products found' : 'No products in this category'}</p>
              {searchQuery && <p className="pos-empty-sub">Try a different search term</p>}
            </div>
          ) : (
            products.map(product => {
              const isTracked = !!product.trackStock;
              const stockQty = product.quantity || 0;
              const outOfStock = isTracked && stockQty <= 0;
              return (
                <button
                  key={product._id || product.id}
                  className={`pos-tile ${outOfStock ? 'pos-tile--oos' : ''} ${lastAddedId === (product._id || product.id) ? 'pos-tile--added' : ''}`}
                  onClick={() => !outOfStock && onAddProduct(product)}
                >
                  {/* Same shape with or without a photo: no photo shows the initials */}
                  {product.images?.[0]?.url ? (
                    <div className="pos-tile-img" style={{ backgroundImage: `url(${product.images[0].url})` }} />
                  ) : (
                    <div className="pos-tile-img pos-tile-img--empty" aria-hidden="true">
                      <span>{initialsOf(product.productName || product.name)}</span>
                    </div>
                  )}
                  <div className="pos-tile-info">
                    <span className="pos-tile-name">{product.productName || product.name}</span>
                    <span className="pos-tile-price">{priceOf(product)}</span>
                    {isTracked && <span className="pos-tile-stock">{stockQty} in stock</span>}
                  </div>
                  {outOfStock && <span className="pos-tile-oos-badge">Out of stock</span>}
                </button>
              );
            })
          )}
        </div>
      )}

      {/* Service cards (grouped by category) */}
      {!loading && activeType === 'services' && (
        <div className="pos-service-grid-wrap">
          {serviceCategories.length === 0 && featuredServices.length === 0 ? (
            <div className="pos-empty">
              <p>No services found</p>
            </div>
          ) : (
            <>
              {/* Featured services */}
              {featuredServices.length > 0 && activeSubCategory === 'all' && (
                <div className="pos-service-section">
                  <div className="pos-service-section-header">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    <span>Featured</span>
                  </div>
                  <div className="pos-service-cards">
                    {featuredServices.map((service, i) => {
                      const sid = service.commonServiceId || service._id || service.serviceId;
                      return (
                        <button
                          key={sid || i}
                          className={`pos-svc-card pos-svc-card--featured ${lastAddedId === sid ? 'pos-tile--added' : ''}`}
                          onClick={() => onAddService(service)}
                        >
                          <div className="pos-svc-card-accent" style={{ backgroundColor: service.diaryColour || '#D6D5EC' }} />
                          <div className="pos-svc-card-body">
                            <span className="pos-svc-card-name">{service.serviceName}</span>
                            <span className="pos-svc-card-price">{fmt(service.servicePrice)}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Category groups */}
              {serviceCategories.map((cat, catIdx) => (
                <div key={catIdx} className="pos-service-section">
                  {activeSubCategory === 'all' && (
                    <div className="pos-service-section-header">
                      <div className="pos-service-section-pip" style={{ backgroundColor: cat.colour }} />
                      <span>{cat.categoryName}</span>
                    </div>
                  )}
                  <div className="pos-service-cards">
                    {cat.items.map((service, i) => {
                      const sid = service.commonServiceId || service._id || service.serviceId;
                      return (
                        <button
                          key={sid || i}
                          className={`pos-svc-card ${lastAddedId === sid ? 'pos-tile--added' : ''}`}
                          onClick={() => onAddService(service)}
                        >
                          <div className="pos-svc-card-accent" style={{ backgroundColor: service.diaryColour || cat.colour }} />
                          <div className="pos-svc-card-body">
                            <span className="pos-svc-card-name">{service.serviceName}</span>
                            <span className="pos-svc-card-price">{fmt(service.servicePrice)}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Voucher tiles */}
      {!loading && activeType === 'vouchers' && (
        <div className="pos-tiles">
          {vouchers.length === 0 ? (
            <div className="pos-empty">
              <p>No vouchers available</p>
            </div>
          ) : (
            vouchers.map((voucher, i) => (
              <button
                key={voucher.id || voucher._id || i}
                className="pos-tile pos-tile--voucher"
                onClick={() => onAddVoucher(voucher)}
              >
                <div className="pos-tile-voucher-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#29282D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="5" width="22" height="14" rx="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                </div>
                <div className="pos-tile-info">
                  <span className="pos-tile-name">{voucher.name || 'Gift Voucher'}</span>
                  <span className="pos-tile-price">{fmt(voucher.value)}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
